import { BlockStack, Button, Checkbox, InlineStack, Text } from '@shopify/polaris';
import type { ThemeAppearance, WidgetColorKey } from '../../app/models/StorefrontResultsSettings';

const fields: { key: WidgetColorKey; label: string; fallback: string; automatic: string }[] = [
    { key: 'primary', label: 'Main action background', fallback: '#104190', automatic: 'Uses the Primary button color in your theme block.' },
    { key: 'primaryText', label: 'Main action text', fallback: '#ffffff', automatic: 'Uses the Primary button text in your theme block.' },
    { key: 'summary', label: 'Product Guide summary background', fallback: '#104190', automatic: 'Uses the selected vehicle background in your theme block.' },
    { key: 'summaryText', label: 'Product Guide summary text', fallback: '#ffffff', automatic: 'Uses the selected vehicle text in your theme block.' },
    { key: 'accent', label: 'Positive fitment accent', fallback: '#087f3f', automatic: 'Default: green (#087f3f).' },
    { key: 'surface', label: 'Panel background', fallback: '#ffffff', automatic: 'Default: white (#ffffff).' },
    { key: 'subtle', label: 'Highlighted background', fallback: '#f3f4f6', automatic: 'Default: very light gray (#f3f4f6).' },
    { key: 'text', label: 'Main text', fallback: '#111111', automatic: 'Default: dark charcoal (#111111).' },
    { key: 'muted', label: 'Secondary text', fallback: '#4b5563', automatic: 'Default: medium-dark gray (#4b5563).' },
    { key: 'border', label: 'Panel and field borders', fallback: '#d1d5db', automatic: 'Default: light gray (#d1d5db).' },
];

export default function DialogWidgetColorsEditor({ appearance, onChange }: {
    appearance: ThemeAppearance;
    onChange: (appearance: ThemeAppearance) => void;
}) {
    const colors = appearance.widgetColors ?? {};
    const setColor = (key: WidgetColorKey, value?: string) => {
        const next = { ...colors };
        if (value === undefined) delete next[key]; else next[key] = value;
        onChange({ ...appearance, widgetColors: next });
    };
    return <details>
        <summary>Advanced: dialog and widget colors</summary>
        <BlockStack gap="400">
            <Text as="p" tone="subdued">
                Native vehicle dialogs, Fit Check, Product Guide and shared compatible-vehicle tables
                follow your header Vehicle Selector, with neutral panels and a green fit-confirmation accent.
                Override individual colors below. These settings do not change the search bar or app results pages.
            </Text>
            <Text as="p" tone="subdued">Save, then reload your theme preview to see these colors. The results preview here does not show these overrides.</Text>
            {fields.map(field => <BlockStack key={field.key} gap="100">
                <Checkbox label={`Override ${field.label.toLowerCase()}`}
                    checked={colors[field.key] !== undefined}
                    onChange={checked => setColor(field.key, checked ? field.fallback : undefined)}
                    helpText={colors[field.key] === undefined ? field.automatic : 'Custom color. Turn off the override to restore the theme color or default.'} />
                {colors[field.key] !== undefined && <InlineStack gap="200" blockAlign="center">
                    <input type="color" aria-label={`${field.label} custom color`} value={colors[field.key]}
                        onChange={event => setColor(field.key, event.currentTarget.value)} />
                    <Text as="span">{colors[field.key]}</Text>
                </InlineStack>}
            </BlockStack>)}
            {Object.keys(colors).length > 0 && <Button onClick={() => onChange({ ...appearance, widgetColors: {} })}>Restore automatic dialog and widget colors</Button>}
        </BlockStack>
    </details>;
}
